import { supabase } from './supabase'
import { UserRole } from '../types/database.types'
import { 
  canManage, 
  canAdmin, 
  isSuperAdmin
} from '../utils/roleUtils'

export interface ValidationResult {
  isValid: boolean
  error?: string
  userRole?: UserRole
  groupRole?: string
}

export interface GroupValidationResult extends ValidationResult {
  groupId: string
  isGroupMember: boolean
  groupRole?: string
}

export class RoleValidationService {
  /**
   * Validate user's global role for a specific permission
   */
  static validateGlobalRole(userId: string, requiredPermission: (role: UserRole) => boolean): Promise<ValidationResult> {
    return new Promise(async (resolve) => {
      try {
        const { data: user, error } = await supabase
          .from('users')
          .select('role, tenant_id')
          .eq('id', userId)
          .single()

        if (error) {
          console.warn('Role validation error:', error)
          // Default to basic permissions if role query fails
          resolve({
            isValid: false,
            error: 'Role validation failed'
          })
          return
        }

        if (!user) {
          resolve({
            isValid: false,
            error: 'User not found'
          })
          return
        }

        const isValid = requiredPermission(user.role as UserRole)
        resolve({
          isValid,
          userRole: user.role as UserRole,
          error: isValid ? undefined : 'Insufficient global permissions'
        })
      } catch (error) {
        console.warn('Role validation exception:', error)
        resolve({
          isValid: false,
          error: 'Failed to validate user role'
        })
      }
    })
  }

  /**
   * Validate user's role in a specific group
   */
  static validateGroupRole(userId: string, groupId: string, requiredPermission: (role: string) => boolean): Promise<GroupValidationResult> {
    return new Promise(async (resolve) => {
      try {
        // First check global role
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('role, tenant_id')
          .eq('id', userId)
          .single()

        if (userError) {
          console.warn('User role query error:', userError)
          // Continue with group membership check even if role query fails
        }

        if (!user) {
          resolve({
            isValid: false,
            error: 'User not found',
            groupId,
            isGroupMember: false
          })
          return
        }

        // Check if user has global permissions
        if (canManage(user.role as UserRole)) {
          resolve({
            isValid: true,
            userRole: user.role as UserRole,
            groupId,
            isGroupMember: true
          })
          return
        }

        // Check group-specific role
        const { data: membership, error: membershipError } = await supabase
          .from('group_members')
          .select('role')
          .eq('user_id', userId)
          .eq('group_id', groupId)
          .single()

        if (membershipError) {
          console.warn('Group membership query error:', membershipError)
          resolve({
            isValid: false,
            error: 'Failed to check group membership',
            userRole: user?.role as UserRole,
            groupId,
            isGroupMember: false
          })
          return
        }

        if (!membership) {
          resolve({
            isValid: false,
            error: 'User is not a member of this group',
            userRole: user?.role as UserRole,
            groupId,
            isGroupMember: false
          })
          return
        }

        const isValid = requiredPermission(membership.role)
        resolve({
          isValid,
          userRole: user?.role as UserRole,
          groupRole: membership.role,
          groupId,
          isGroupMember: true,
          error: isValid ? undefined : 'Insufficient group permissions'
        })
      } catch (error) {
        resolve({
          isValid: false,
          error: 'Failed to validate group role',
          groupId,
          isGroupMember: false
        })
      }
    })
  }

  /**
   * Validate admin/manager permissions for group operations
   */
  static validateGroupManagement(userId: string, groupId: string): Promise<GroupValidationResult> {
    return this.validateGroupRole(userId, groupId, (role) => ['admin', 'manager'].includes(role))
  }

  /**
   * Validate curriculum management permissions
   */
  static validateCurriculumManagement(userId: string, groupId: string): Promise<GroupValidationResult> {
    return this.validateGroupRole(userId, groupId, (role) => ['admin', 'manager', 'member'].includes(role))
  }

  /**
   * Validate event management permissions
   */
  static validateEventManagement(userId: string, groupId: string): Promise<GroupValidationResult> {
    return this.validateGroupRole(userId, groupId, (role) => ['admin', 'manager'].includes(role))
  }

  /**
   * Validate content deletion permissions
   */
  static validateContentDeletion(userId: string, groupId: string): Promise<GroupValidationResult> {
    return this.validateGroupRole(userId, groupId, (role) => ['admin'].includes(role))
  }

  /**
   * Validate announcement management permissions
   */
  static validateAnnouncementManagement(userId: string, groupId: string): Promise<GroupValidationResult> {
    return this.validateGroupRole(userId, groupId, (role) => ['admin', 'manager'].includes(role))
  }

  /**
   * Validate analytics viewing permissions
   */
  static validateAnalyticsAccess(userId: string, groupId: string): Promise<GroupValidationResult> {
    return this.validateGroupRole(userId, groupId, (role) => ['admin'].includes(role))
  }

  /**
   * Validate group settings management permissions
   */
  static validateGroupSettingsManagement(userId: string, groupId: string): Promise<GroupValidationResult> {
    return this.validateGroupRole(userId, groupId, (role) => ['admin', 'manager'].includes(role))
  }

  /**
   * Validate task assignment permissions
   */
  static validateTaskAssignment(userId: string, groupId: string): Promise<GroupValidationResult> {
    return this.validateGroupRole(userId, groupId, (role) => ['admin', 'manager', 'member'].includes(role))
  }

  /**
   * Validate group data viewing permissions
   */
  static validateGroupDataAccess(userId: string, groupId: string): Promise<GroupValidationResult> {
    return this.validateGroupRole(userId, groupId, (role) => ['admin', 'manager', 'member'].includes(role))
  }

  /**
   * Validate super admin permissions
   */
  static validateSuperAdmin(userId: string): Promise<ValidationResult> {
    return this.validateGlobalRole(userId, isSuperAdmin)
  }

  /**
   * Validate admin permissions
   */
  static validateAdmin(userId: string): Promise<ValidationResult> {
    return this.validateGlobalRole(userId, canAdmin)
  }

  /**
   * Validate group membership
   */
  static validateGroupMembership(userId: string, groupId: string): Promise<GroupValidationResult> {
    return new Promise(async (resolve) => {
      try {
        const { data: membership, error } = await supabase
          .from('group_members')
          .select('role')
          .eq('user_id', userId)
          .eq('group_id', groupId)
          .single()

        if (error || !membership) {
          resolve({
            isValid: false,
            error: 'User is not a member of this group',
            groupId,
            isGroupMember: false
          })
          return
        }

        resolve({
          isValid: true,
          groupRole: membership.role,
          groupId,
          isGroupMember: true
        })
      } catch (error) {
        resolve({
          isValid: false,
          error: 'Failed to validate group membership',
          groupId,
          isGroupMember: false
        })
      }
    })
  }

  /**
   * Validate ownership of a resource
   */
  static validateResourceOwnership(userId: string, resourceUserId: string): Promise<ValidationResult> {
    return new Promise((resolve) => {
      const isValid = userId === resourceUserId
      resolve({
        isValid,
        error: isValid ? undefined : 'You can only manage your own resources'
      })
    })
  }

  /**
   * Validate resource access in group context
   */
  static validateGroupResourceAccess(
    userId: string, 
    groupId: string, 
    resourceUserId: string
  ): Promise<GroupValidationResult> {
    return new Promise(async (resolve) => {
      try {
        // Check if user owns the resource
        if (userId === resourceUserId) {
          resolve({
            isValid: true,
            groupId,
            isGroupMember: true
          })
          return
        }

        // Check if user has admin/manager permissions
        const validation = await this.validateGroupManagement(userId, groupId)
        resolve(validation)
      } catch (error) {
        resolve({
          isValid: false,
          error: 'Failed to validate resource access',
          groupId,
          isGroupMember: false
        })
      }
    })
  }
} 